import type { ProblemDetails } from './types'

/**
 * Every API failure surfaces as this error so screens can branch on the status
 * and always have a trace id to show the user (§9, §14).
 */
export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails
  readonly retryAfterSeconds: number | null

  constructor(problem: ProblemDetails, retryAfterSeconds: number | null = null) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
    this.retryAfterSeconds = retryAfterSeconds
  }

  get traceId(): string | undefined {
    return this.problem.traceId
  }

  get isOffline(): boolean {
    return this.status === 0
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isAccountBlocked(): boolean {
    return this.problem.type.endsWith('account-blocked')
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }
}

export function offlineError(): ApiError {
  return new ApiError({
    type: 'https://library.itpu.uz/problems/offline',
    title: 'Network unavailable',
    status: 0,
  })
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}
